import { Entity } from '@backstage/catalog-model';
import { GlitchtipUptimeResult, GlitchtipErrorResult, Sev2ProjectResult } from '../types';
import { GLITCHTIP_HEALTH_CHECK_URL, GLITCHTIP_ERRORS_CHECK_URL, SEV2_PROJECT_BOARD_URL } from '../constants';

export const useUptimeMonitorEntity = (entity: Entity): GlitchtipUptimeResult => {
  try {
    const url = entity.metadata?.annotations?.[GLITCHTIP_HEALTH_CHECK_URL];

    if (!url) {
      return {
        success: false,
        error: 'No health check URL annotation found. Please add the glitchtip.io/health-check-url annotation to your entity.',
        data: null,
      };
    }

    // Validate URL format
    let urlObj: URL;
    try {
      urlObj = new URL(url);
    } catch {
      return {
        success: false,
        error: 'Invalid URL format in health check annotation.',
        data: null,
      };
    }

    const host = urlObj.hostname;

    // Extract org and id from URL
    const urlPattern = /\/([^\/]+)\/uptime-monitors\/(\d+)/;
    const match = url.match(urlPattern);

    if (!match) {
      return {
        success: false,
        error: 'URL format does not match expected pattern: /org/uptime-monitors/id',
        data: null,
      };
    }

    const org = match[1];
    const monitorId = match[2];

    return {
      success: true,
      data: {
        healthCheckUrl: url,
        host,
        org,
        monitorId,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Unexpected error parsing entity: ${error instanceof Error ? error.message : 'Unknown error'}`,
      data: null,
    };
  }
};

export const useUptimeErrorsEntity = (entity: Entity): GlitchtipErrorResult => {
  try {
    const url = entity.metadata?.annotations?.[GLITCHTIP_ERRORS_CHECK_URL];

    if (!url) {
      return {
        success: false,
        error: 'No errors check URL annotation found. Please add the glitchtip.io/errors-check-url annotation to your entity.',
        data: null,
      };
    }

    // Validate URL format
    let urlObj: URL;
    try {
      urlObj = new URL(url);
    } catch {
      return {
        success: false,
        error: 'Invalid URL format in errors check annotation.',
        data: null,
      };
    }

    const host = urlObj.hostname;

    // Extract org and project ID from URL
    const urlPattern = /\/([^\/]+)\/issues\?project=(\d+)/;
    const match = url.match(urlPattern);

    if (!match) {
      return {
        success: false,
        error: 'URL format does not match expected pattern: /org/issues?project=id',
        data: null,
      };
    }

    const org = match[1];
    const projectId = match[2];

    return {
      success: true,
      data: {
        errorsCheckUrl: url,
        host,
        org,
        projectId,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Unexpected error parsing entity: ${error instanceof Error ? error.message : 'Unknown error'}`,
      data: null,
    };
  }
};

export const useSev2ProjectEntity = (entity: Entity): Sev2ProjectResult => {
  try {
    const url = entity.metadata?.annotations?.[SEV2_PROJECT_BOARD_URL];

    if (!url) {
      return {
        success: false,
        error: 'No sev2 project board URL annotation found. Please add the sev2/project-board-url annotation to your entity.',
        data: null,
      };
    }

    const urlPattern = /\/project\/view\/(\d+)\/?$/;
    const match = url.match(urlPattern);

    if (!match) {
      return {
        success: false,
        error: 'URL format does not match expected pattern: /project/view/id',
        data: null,
      };
    }

    const projectId = match[1];

    return {
      success: true,
      data: {
        projectBoardUrl: url,
        projectId,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Unexpected error parsing sev2 entity: ${error instanceof Error ? error.message : 'Unknown error'}`,
      data: null,
    };
  }
};